import { defineStore } from "pinia";
import { searchPosts } from "@/fetch";
import { sortTerms } from "@/utils";
import { months } from "./locales";

export type Term = {
  id: number;
  slug: string;
  name: string;
  order: number;
  children: Array<Term>;
};
export type Year = { year: number; months: Array<number> };
export type Image = { alt: string; medium: string; large: string };
export type Item = {
  id: number;
  lang: string;
  title: string;
  date: string;
  author: string;
  link: string;
  image: Image;
  preview: string;
  data: Record<string, string>;
  primary: Array<Term>;
  secondary: Array<Term>;
  search: Array<string>;
};

export const useItemStore = defineStore("items", {
  state: () => ({
    type: "",
    lang: "",
    total: 0,
    perPage: 8,
    show: 8,
    page: 1,
    year: 0,
    month: 0,
    filterRadio: true,
    filter: {
      primary: [] as Array<string>,
      secondary: [] as Array<string>,
    },
    taxonomy: {
      primary: "",
      secondary: "",
    },
    terms: {
      primary: [] as Array<Term>,
      secondary: [] as Array<Term>,
    },
    months: [] as Array<Year>,
    items: [] as Array<Item>,
    historyInhibit: false,
    search: "",
    asc: true,
    sort: "date",
    showSearch: true,
    showSort: true,
    showMonth: false,
    showFields: ["author", "date", "term"],
    showImage: true,
    showPreview: true,
    hasLink: true,
    readMore: "",
    imagePostcard: true,
    filterTerm: [] as Array<Term>,
  }),
  getters: {
    /**
     * Filters out items according to given criteria.
     * @returns {Array<Item>}
     */
    filterItems: function (state): Array<Item> {
      return state.items.filter((item) => {
        if (item.lang !== state.lang) {
          return false;
        }
        if (state.filter.primary.length > 0) {
          let hit = false;
          item.primary.forEach((term) => {
            if (state.filter.primary.includes(term.slug)) hit = true;
          });
          if (!hit) return false;
        }
        if (state.filter.secondary.length > 0) {
          let hit = false;
          item.secondary.forEach((term) => {
            if (state.filter.secondary.includes(term.slug)) hit = true;
          });
          if (!hit) return false;
        }

        if (state.search.length > 0) {
          return (
            item.title.toLowerCase().includes(state.search.toLowerCase()) ||
            item.search.includes(state.search.toLowerCase())
          );
        }
        if (state.year > 0) {
          const date = new Date(item.date);
          if (date.getFullYear() != state.year) {
            return false;
          }
          if (state.month > 0) {
            if (date.getMonth() + 1 != state.month) {
              return false;
            }
          }
          console.debug(date);
        }
        return true;
      });
    },
    /**
     * Sort and limit the filtered list.
     * @returns {Array<Item>}
     */
    getItems: function (state): Array<Item> {
      return this.filterItems
        .sort((a, b) => {
          if (state.sort == "title") {
            if (a.title === b.title) return 0;
            if (a.title > b.title) return state.asc ? 1 : -1;
            return state.asc ? -1 : 1;
          } else {
            if (a.date === b.date) return 0;
            if (a.date > b.date) return -1;
            return 1;
          }
        })
        .slice(0, state.show);
    },
    getPrimary: function (state): Array<Term> {
      return sortTerms(state.terms.primary);
    },
    getSecondary: function (state): Array<Term> {
      return sortTerms(state.terms.secondary);
    },
    /**
     * Check if there is more items to show.
     * @returns {boolean}
     */
    hasMore: function (state): boolean {
      return state.total > state.show && this.filterItems.length >= state.show;
    },
    getMonth: function (state): string {
      return months(state.lang)[state.month];
    },
    getMonths: function (state): Array<number> {
      if (state.year == 0) {
        return [];
      }
      for (const year of state.months) {
        if (state.year == year.year) {
          return year.months;
        }
      }
      return [];
    },
  },
  actions: {
    setFilter(taxonomy: string, slug: string) {
      const filter =
        taxonomy === this.taxonomy.primary
          ? this.filter.primary
          : this.filter.secondary;
      if (this.filterRadio) {
        // Replace all old values with the new value.
        filter.length = 0;
        if (slug) {
          filter.push(slug);
        }
      } else {
        if (filter.includes(slug)) {
          // Remove the item.
          for (let i = 0; i < filter.length; i++) {
            if (filter[i] === slug) {
              filter.splice(i, 1);
              break;
            }
          }
        } else {
          // Add the item.
          filter.push(slug);
        }
      }

      /*
       * Set the filter as hash path.
       */
      // Set this to inhibit update from history change.
      this.historyInhibit = true;
      // Make a string of all the active filters.
      const hashSource = [
        this.filter.primary.join(","),
        this.filter.secondary.join(","),
      ];
      window.location.hash = hashSource.join("/");
      const uri = window.location.toString();
      if (
        this.filter.primary.length + this.filter.secondary.length === 0 &&
        uri.indexOf("#") > 0
      ) {
        window.history.replaceState(
          {},
          document.title,
          uri.substring(0, uri.indexOf("#"))
        );
      }

      // Reset "pagination"
      this.resetShow();
      // Fetch new data from backend.
      searchPosts();
    },
    addItem(item: Item) {
      let found = false;
      this.items.forEach((i) => {
        if (i.id === item.id) {
          found = true;
          if (item.search.length > 0) {
            i.search.push(...item.search);
          }
        }
      });
      if (!found) {
        // Add item if not in store.
        this.items.push(item);
      }
    },
    addTerms(
      terms: Array<{
        id: number;
        slug: string;
        name: string;
        order: number;
        parent: number;
      }>,
      taxonomy: string
    ) {
      const categories = [] as Array<Term>;

      console.debug(terms);

      for (const term of terms) {
        if (term.parent == 0) {
          categories.push({ ...term, children: [] } as Term);
        }
      }
      for (const term of terms) {
        if (term.parent > 0) {
          categories.forEach((c) => {
            if (c.id == term.parent) {
              c.children.push({ ...term, children: [] } as Term);
            }
          });
        }
      }
      if (this.taxonomy.primary === taxonomy) {
        this.terms.primary = categories;
      } else {
        this.terms.secondary = categories;
      }
    },
    toggleSort() {
      this.asc = !this.asc;
      // Reset "pagination"
      this.resetShow();
      // Fetch new data from backend.
      searchPosts();
    },
    setYear(year: number) {
      if (this.year != year) {
        this.month = 0;
      }
      this.year = year;
      searchPosts();
    },
    setMonth(month: number) {
      this.month = month;
      searchPosts();
    },
    showMore() {
      this.show += this.perPage;
      // Backend uses twice the perPage value as page size.
      const haveGotten = this.page * this.perPage * 2;
      // Check that there is always an extra page of items gotten. But cap value at total items.
      const toGet =
        this.show + this.perPage > this.total
          ? this.total
          : this.show + this.perPage;
      if (this.page > 0 && haveGotten < toGet) {
        this.page++;
        searchPosts();
      }
    },
    resetShow() {
      this.page = 1;
      this.show = this.perPage;
    },
  },
});
